import Pagination from '@mui/material/Pagination';

import Button from '@components/button/Button';
import { PAGINATION_LIMIT } from '@constants/generic';

import PageNumberInput from './PageNumberInput';

import theme from '../../../themes/colors';

interface CustomTablePaginationProps {
  currentPage: number;
  totalCount: number;
  onPageChange: (count: number) => void;
  isFixed?: boolean;
  paginationLimit?: number;
  showGoToPage?: boolean;
}

const CustomTablePagination = ({
  currentPage,
  totalCount,
  onPageChange,
  isFixed = true,
  paginationLimit = PAGINATION_LIMIT,
  showGoToPage = false
}: CustomTablePaginationProps) => {
  const pageCount = Math.ceil(totalCount / paginationLimit);

  const handlePageChange = (_event: any, value: number) => {
    onPageChange(value);
  };

  const baseClassName = isFixed
    ? 'fixed bottom-0 left-55 flex h-17 w-[calc(100%-220px)] items-center px-6 bg-cofeeNeutral1'
    : 'flex items-center';

  if (pageCount <= 1) return null;

  const previousButton = (
    <Button
      label="Previous"
      handleClick={() => onPageChange(currentPage - 1)}
      leftIconName="arrow-right-grey"
      variant="tertiary"
      disabled={currentPage === 1}
      className="!h-9 !bg-cofeeNeutral1 !text-sm !font-medium"
      labelStyle={`${
        currentPage === 1 ? '!text-cofeeNeutral5' : '!text-cofeeNeutral10'
      }`}
      leftIconStyle={`rotate-180
        ${
          currentPage === 1 ? '!stroke-cofeeNeutral5' : '!stroke-cofeeNeutral10'
        }
      `}
    />
  );

  const pageNumbers = (
    <Pagination
      count={pageCount}
      page={currentPage}
      onChange={handlePageChange}
      shape="rounded"
      hideNextButton
      hidePrevButton
      siblingCount={1}
      boundaryCount={1}
      sx={{
        '& .MuiPagination-ul': {
          flexWrap: 'nowrap'
        },
        '& .MuiPaginationItem-root': {
          border: `1px solid ${theme.cofeeNeutral4}`,
          backgroundColor: theme.cofeeNeutral1,
          color: theme.cofeeNeutral10,
          height: '40px',
          width: '40px',
          placeContent: 'center',
          borderRadius: '8px'
        },
        '& .MuiPaginationItem-root.Mui-selected': {
          border: `1px solid ${theme.cofeePrimary4}`,
          color: theme.cofeePrimary6,
          backgroundColor: theme.cofeeNeutral1
        },
        '& .MuiPaginationItem-root:hover': {
          backgroundColor: theme.cofeeNeutral1
        }
      }}
    />
  );

  const nextButton = (
    <Button
      label="Next"
      handleClick={() => onPageChange(currentPage + 1)}
      rightIconName="arrow-right-grey"
      variant="tertiary"
      disabled={currentPage === pageCount}
      className="!mr-2 !h-9 !bg-cofeeNeutral1 !text-sm !font-medium"
      labelStyle={
        currentPage === pageCount
          ? '!text-cofeeNeutral5'
          : '!text-cofeeNeutral10'
      }
      rightIconStyle={
        currentPage === pageCount
          ? '!stroke-cofeeNeutral5'
          : '!stroke-cofeeNeutral10'
      }
    />
  );

  if (showGoToPage) {
    return (
      <div
        className={`${baseClassName} flex-wrap justify-center gap-x-6 gap-y-3 ${
          isFixed ? '!h-auto min-h-17 py-2' : 'mt-3'
        }`}
      >
        <div className="flex flex-1 items-center justify-center gap-5">
          {previousButton}
          {pageNumbers}
          {nextButton}
        </div>
        <PageNumberInput maxPage={pageCount} onPageChange={onPageChange} />
      </div>
    );
  }

  return (
    <div
      className={`${baseClassName} justify-between ${isFixed ? '' : 'mt-3'}`}
    >
      {previousButton}
      {pageNumbers}
      {nextButton}
    </div>
  );
};

export default CustomTablePagination;
